import React from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { MoneyOff } from '@material-ui/icons';
import moment from 'moment';
import 'moment/locale/ru';
import { numberWithSpaces } from 'helper';
import { IKSKDebtModel } from 'interfaces';
import DownloadActButton from './DownloadActButton';
import EmptyState from './EmptyState';

const useStyles = makeStyles({
  monthHeader: {
    display: 'flex',
    backgroundColor: '#3f51b5',
    color: '#fff',
    padding: 8,
    borderRadius: '4px 4px 0 0',
  },
  table: {
    border: '1px solid #e0e0e0',
    borderTop: 0,
    marginBottom: 24,
  },
  boldCell: {
    fontWeight: 'bold',
  },
  stripeRow: {
    '&:nth-of-type(even)': {
      backgroundColor: '#fafafa',
    },
  },
});

interface DebtDetailProps {
  label: string;
  subLabel: string;
  info: IKSKDebtModel | null;
  loading: boolean;
  realEstateGuid?: string;
  contractGuid?: string;
  ownerGuid1c?: string;
}

interface MonthGroup {
  monthKey: string;
  monthLabel: string;
  items: { type: string; startSumma: number; added: number; paid: number; endSumma: number }[];
  totalStart: number;
  totalAdded: number;
  totalPaid: number;
  totalEnd: number;
}

const groupByMonth = (items: IKSKDebtModel['informationOfDebt']): MonthGroup[] => {
  const map = new Map<string, MonthGroup>();
  for (const item of items) {
    const date = new Date(item.month);
    const m = date.getMonth() + 1;
    const y = date.getFullYear();
    const key = `${m}.${y}`;

    if (!map.has(key)) {
      map.set(key, {
        monthKey: key,
        monthLabel: moment(date).locale('ru').format('MMMM YYYY') + ' г.',
        items: [],
        totalStart: 0,
        totalAdded: 0,
        totalPaid: 0,
        totalEnd: 0,
      });
    }

    const group = map.get(key)!;
    group.items.push({
      type: item.type,
      startSumma: item.startSumma,
      added: item.added,
      paid: item.paid,
      endSumma: item.endSumma,
    });
    group.totalStart += item.startSumma;
    group.totalAdded += item.added;
    group.totalPaid += item.paid;
    group.totalEnd += item.endSumma;
  }

  return Array.from(map.values()).sort((a, b) => {
    const [am, ay] = a.monthKey.split('.').map(Number);
    const [bm, by] = b.monthKey.split('.').map(Number);
    return by - ay || bm - am;
  });
};

const DebtDetail: React.FC<DebtDetailProps> = ({
  label,
  subLabel,
  info,
  loading,
  realEstateGuid,
  contractGuid,
  ownerGuid1c,
}) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const headerSummary = [label, subLabel].filter(Boolean).join(' • ');
  const isEmpty = !info || info.informationOfDebt.length === 0;
  const monthGroups = info ? groupByMonth(info.informationOfDebt) : [];

  const canDownload = Boolean(realEstateGuid && contractGuid && ownerGuid1c);

  return (
    <Box>
      {headerSummary && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {headerSummary}
        </Typography>
      )}

      {info && !isEmpty && (
        <Box display="flex" style={{ gap: 24 }} mb={2} flexWrap="wrap">
          {info.ercNumber && (
            <Typography variant="body2">
              ЛС ЕРЦ: <b>{info.ercNumber}</b>
            </Typography>
          )}
          {info.accountNumber && (
            <Typography variant="body2">
              ЛС BI-Group: <b>{info.accountNumber}</b>
            </Typography>
          )}
          {info.ivcNumber && (
            <Typography variant="body2">
              ЛС ИВЦ: <b>{info.ivcNumber}</b>
            </Typography>
          )}
          {info.alsekoNumber && (
            <Typography variant="body2">
              ЛС Алсеко: <b>{info.alsekoNumber}</b>
            </Typography>
          )}
        </Box>
      )}

      {canDownload && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <DownloadActButton
            realEstateGuid={realEstateGuid!}
            contractGuid={contractGuid!}
            ownerGuid1c={ownerGuid1c!}
          />
        </Box>
      )}

      {isEmpty && (
        <EmptyState
          icon={<MoneyOff style={{ fontSize: 64, color: '#bdbdbd', marginBottom: 16 }} />}
          title="Данные о задолженности отсутствуют"
          subtitle="По выбранному критерию информация не найдена"
        />
      )}

      {!isEmpty &&
        monthGroups.map((group) => (
          <Box key={group.monthKey} mb={3}>
            <Box className={classes.monthHeader}>
              <Box flex={1}>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#fff' }}>
                  {group.monthLabel}
                </Typography>
              </Box>
              <Box style={{ width: 130, textAlign: 'right' }}>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#fff' }}>
                  {numberWithSpaces(group.totalStart.toFixed(2))} тг
                </Typography>
              </Box>
              <Box style={{ width: 130, textAlign: 'right' }}>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#fff' }}>
                  {numberWithSpaces(group.totalAdded.toFixed(2))} тг
                </Typography>
              </Box>
              <Box style={{ width: 130, textAlign: 'right' }}>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#fff' }}>
                  {numberWithSpaces(group.totalPaid.toFixed(2))} тг
                </Typography>
              </Box>
              <Box style={{ width: 130, textAlign: 'right' }}>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#fff' }}>
                  {numberWithSpaces(group.totalEnd.toFixed(2))} тг
                </Typography>
              </Box>
            </Box>

            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.boldCell}>Вид начисления</TableCell>
                  <TableCell className={classes.boldCell} align="right">Долг</TableCell>
                  <TableCell className={classes.boldCell} align="right">Начислено</TableCell>
                  <TableCell className={classes.boldCell} align="right">Оплачено</TableCell>
                  <TableCell className={classes.boldCell} align="right">К оплате</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.items.map((item, idx) => (
                  <TableRow
                    key={`${group.monthKey}-${item.type}-${idx}`}
                    className={classes.stripeRow}
                  >
                    <TableCell>{item.type}</TableCell>
                    <TableCell align="right">
                      {numberWithSpaces(item.startSumma.toFixed(2))} тг
                    </TableCell>
                    <TableCell align="right">
                      {numberWithSpaces(item.added.toFixed(2))} тг
                    </TableCell>
                    <TableCell align="right">
                      {numberWithSpaces(item.paid.toFixed(2))} тг
                    </TableCell>
                    <TableCell align="right">
                      {numberWithSpaces(item.endSumma.toFixed(2))} тг
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}
    </Box>
  );
};

export default DebtDetail;
